import toast from "react-hot-toast";

import { i18n } from "simplydo/core/i18n";
import { OpenAPI } from "simplydo/interfaces";

import api from "api";
import util from "utils/utils";

export const addTag = (
  idea: OpenAPI.Schemas["Idea"],
  newTag: OpenAPI.Schemas["Tag"],
) => {
  api.tags.tagSingle(
    "ideas",
    newTag._id,
    idea._id,
    (updatedTag) => {
      util.onUpdateIdea(idea, {
        tags: [...(idea?.tags || []), updatedTag._id],
        ownerTags: [...(idea?.ownerTags || []), updatedTag],
      });

      // Invalidate challenge to reload tag list
      api.invalidateQueries(api.challenges.get, [idea.challenge]);
    },
    () => {},
  );
};

export const removeTag = (
  idea: OpenAPI.Schemas["Idea"],
  tag: OpenAPI.Schemas["Tag"],
) => {
  api.tags.untagSingle(
    "ideas",
    tag._id,
    idea._id,
    (updatedTag) => {
      util.onUpdateIdea(idea, {
        tags: (idea?.tags || []).filter((t) => t !== updatedTag._id),
        ownerTags: (idea?.ownerTags || []).filter(
          (t) => t._id !== updatedTag._id,
        ),
      });

      // Invalidate challenge to reload tag list
      api.invalidateQueries(api.challenges.get, [idea.challenge]);
    },
    () => {},
  );
};

export const labelIdea = (
  idea: OpenAPI.Schemas["Idea"],
  label: string,
  isApplied: boolean,
) => {
  const { labels = [] } = idea;
  const newLabels = [...(labels || [])].filter((l) => l !== label);
  if (isApplied) {
    newLabels.push(label);
  }

  api.ideas.updateStatus(
    idea._id,
    { labels: newLabels },
    () => {
      api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
        ...prev,
        ideas: prev.ideas.map((prevIdea) => {
          if (idea._id !== prevIdea._id) {
            return prevIdea;
          }
          return { ...prevIdea, labels: newLabels };
        }),
      }));
    },
    () => {
      toast.error("Failed to apply label");
    },
  );
};

export const voteForIdea = (
  idea: OpenAPI.Schemas["Idea"],
  addVote: boolean,
) => {
  api.ideas.vote(
    idea._id,
    addVote,
    ({ addedVotesFor, removedVotesFor }) => {
      const countIdeasVoted = addedVotesFor.length - removedVotesFor.length;

      util.onUpdateIdea(idea, {
        voteCount: (idea?.voteCount || 0) + (addVote ? 1 : -1),
        votedFor: addVote,
      });
      api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
        ...prev,
        ideasVotedFor: (prev?.ideasVotedFor || 0) + countIdeasVoted,
      }));
    },
    (err) => {
      toast.error(err.message);
    },
  );
};

export const pinIdea = (idea: OpenAPI.Schemas["Idea"], isPinned: boolean) => {
  api.ideas.updateStatus(
    idea._id,
    { isPinned },
    () => {
      toast.success(
        isPinned ?
          `${i18n.t("common:capitalise", { key: "generic.idea" })} pinned`
        : `${i18n.t("common:capitalise", { key: "generic.idea" })} unpinned`,
      );

      api.setQueryData(
        api.challenges.getIdeas,
        [idea.challenge],
        (prev) => ({
          ...prev,
          ideas: (prev?.ideas || []).map((prevIdea) =>
            prevIdea._id === idea._id ? { ...prevIdea, isPinned } : prevIdea,
          ),
        }),
        { allowUndefined: true },
      );
      api.invalidateQueries(api.challenges.getIdeas, [idea.challenge]);
    },
    () => {
      toast.error("Failed to pin idea");
    },
  );
};
